import React, { useRef, useState, useMemo, useEffect } from "react";
import { ScanFields as ScanFieldsType } from "../types";
import { Line } from "../types";
import { useSubinventoryLov } from "../hooks/useSubinventoryLov";
import { useLocatorLov } from "../hooks/useLocatorLov";

interface ScanFieldsProps {
  scanFields: ScanFieldsType;
  onScanFieldsChange: (fields: ScanFieldsType) => void;
  onSerialNumberScan: (serialNumber: string) => void;
  isDarkTheme?: boolean;
  selectedHeaderToOrg?: string;
  lines?: Line[];
  onAddRow?: () => void;
  preLoadedSubinventories?: string[];
}

const ScanFields: React.FC<ScanFieldsProps> = ({
  scanFields,
  onScanFieldsChange,
  onSerialNumberScan,
  isDarkTheme,
  selectedHeaderToOrg,
  lines = [],
  onAddRow,
  preLoadedSubinventories = [],
}) => {
  const subinventories = preLoadedSubinventories;
  const { data: locators, isLoading: locatorsLoading, refetch: refetchLocators } = useLocatorLov(
    selectedHeaderToOrg,
    scanFields.toSubinventory,
    false
  );
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [showSerialDropdown, setShowSerialDropdown] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const availableSerialNumbers = useMemo(() => {
    return lines.map((line) => line.serialNumber);
  }, [lines]);

  const filteredSerialNumbers = useMemo(() => {
    if (!scanFields.scanSerialNumber) return availableSerialNumbers;
    return availableSerialNumbers.filter((serial) =>
      serial.toLowerCase().includes(scanFields.scanSerialNumber.toLowerCase())
    );
  }, [availableSerialNumbers, scanFields.scanSerialNumber]);
  const handleSerialNumberChange = (value: string) => {
    const updatedFields = { ...scanFields, scanSerialNumber: value };
    onScanFieldsChange(updatedFields);
    setShowSerialDropdown(value.length > 0);
    
    // Clear previous timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Auto-select after user stops typing
    if (value.trim()) {
      timeoutRef.current = setTimeout(() => {
        // Check for exact match first
        const exactMatch = availableSerialNumbers.find(serial => 
          serial.toLowerCase() === value.toLowerCase()
        );
        
        if (exactMatch) {
          onSerialNumberScan(exactMatch);
          setShowSerialDropdown(false);
        } else {
          // Check if only one match remains after user stops typing
          const filtered = availableSerialNumbers.filter(serial =>
            serial.toLowerCase().includes(value.toLowerCase())
          );
          
          if (filtered.length === 1) {
            const updatedFields = { ...scanFields, scanSerialNumber: filtered[0] };
            onScanFieldsChange(updatedFields);
            onSerialNumberScan(filtered[0]);
            setShowSerialDropdown(false);
          }
        }
      }, 500); // Increased timeout to 500ms to give user more time to type
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && scanFields.scanSerialNumber.trim()) {
      onSerialNumberScan(scanFields.scanSerialNumber.trim());
      setShowSerialDropdown(false);
    }
  };

  const handleSerialSelect = (serialNumber: string) => {
    const updatedFields = { ...scanFields, scanSerialNumber: serialNumber };
    onScanFieldsChange(updatedFields);
    onSerialNumberScan(serialNumber);
    setShowSerialDropdown(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowSerialDropdown(false);
      }
    };

    if (showSerialDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSerialDropdown]);

  // Close dropdown when serial number is cleared
  useEffect(() => {
    if (!scanFields.scanSerialNumber) {
      setShowSerialDropdown(false);
    }
  }, [scanFields.scanSerialNumber]);



  return (
    <div
      style={{
        marginBottom: "10px",
        padding: "15px",
        border: `1px solid ${isDarkTheme ? "#555" : "#ccc"}`,
        borderRadius: "4px",
        backgroundColor: isDarkTheme ? "#2a2a2a" : "#fff",
        width: "fit-content",
      }}
    >
      <div style={{ display: "flex", gap: "15px", alignItems: "center" }}>
        <div>
          <select
            value={scanFields.toSubinventory}
            onChange={(e) => {
              const newSubinventory = e.target.value;
              onScanFieldsChange({
                ...scanFields,
                toSubinventory: newSubinventory,
                toLocator: "", // Clear locator when subinventory changes
              });
              // Immediately fetch locators for new subinventory
              if (selectedHeaderToOrg && newSubinventory) {
                refetchLocators(selectedHeaderToOrg, newSubinventory);
              }
            }}
            disabled={false}
            style={{
              padding: "8px",
              border: `1px solid ${isDarkTheme ? "#555" : "#ccc"}`,
              borderRadius: "4px",
              width: "216px",
              backgroundColor: isDarkTheme ? "#333" : "#fff",
              color: isDarkTheme ? "#fff" : "#000",
            }}
          >
            <option value="">Select subinventory</option>
            {subinventories.map((subinventory) => (
              <option key={subinventory} value={subinventory}>
                {subinventory}
              </option>
            ))}
          </select>
        </div>
        <div>
          <select
            value={scanFields.toLocator}
            onChange={(e) =>
              onScanFieldsChange({ ...scanFields, toLocator: e.target.value })
            }
            onFocus={() => {
              if (selectedHeaderToOrg && scanFields.toSubinventory && locators.length === 0) {
                refetchLocators(selectedHeaderToOrg, scanFields.toSubinventory);
              }
            }}
            disabled={locatorsLoading || !scanFields.toSubinventory}
            style={{
              padding: "8px",
              border: `1px solid ${isDarkTheme ? "#555" : "#ccc"}`,
              borderRadius: "4px",
              width: "250px",
              backgroundColor: isDarkTheme ? "#333" : "#fff",
              color: isDarkTheme ? "#fff" : "#000",
            }}
          >
            <option value="">Select locator</option>
            {locators.map((locator) => (
              <option key={locator} value={locator}>
                {locator}
              </option>
            ))}
          </select>
        </div>
        <div style={{ position: "relative" }}>
          <input
            type="text"
            value={scanFields.scanSerialNumber}
            onChange={(e) => handleSerialNumberChange(e.target.value)}
            onKeyPress={handleKeyPress}
            onFocus={() =>
              scanFields.scanSerialNumber && setShowSerialDropdown(true)
            }
            placeholder="Scan or enter serial number"
            style={{
              padding: "8px",
              paddingRight: scanFields.scanSerialNumber ? "30px" : "8px",
              border: `1px solid ${isDarkTheme ? "#555" : "#ccc"}`,
              borderRadius: "4px",
              width: "200px",
              backgroundColor: isDarkTheme ? "#333" : "#fff",
              color: isDarkTheme ? "#fff" : "#000",
              boxSizing: "border-box",
            }}
          />
          {scanFields.scanSerialNumber && (
            <span
              onClick={() => {
                const updatedFields = { ...scanFields, scanSerialNumber: "" };
                onScanFieldsChange(updatedFields);
                setShowSerialDropdown(false);
              }}
              style={{
                position: "absolute",
                right: "8px",
                top: "50%",
                transform: "translateY(-50%)",
                cursor: "pointer",
                color: isDarkTheme ? "#ccc" : "#666",
                fontSize: "20px",
                fontWeight: "bold",
              }}
            >
              ×
            </span>
          )}
          {showSerialDropdown && filteredSerialNumbers.length > 0 && (
            <div
              ref={dropdownRef}
              style={{
                position: "absolute",
                top: "100%",
                left: "0",
                backgroundColor: isDarkTheme ? "#333" : "#fff",
                border: `1px solid ${isDarkTheme ? "#555" : "#ccc"}`,
                borderRadius: "4px",
                zIndex: 1000,
                width: "200px",
                maxHeight: "200px",
                overflowY: "auto",
              }}
            >
              {filteredSerialNumbers.map((serialNum) => (
                <div
                  key={serialNum}
                  onClick={() => handleSerialSelect(serialNum)}
                  style={{
                    padding: "8px 12px",
                    cursor: "pointer",
                    backgroundColor: "transparent",
                    color: isDarkTheme ? "#fff" : "#000",
                    borderBottom: `1px solid ${isDarkTheme ? "#555" : "#eee"}`,
                    fontSize: "12px",
                    fontFamily: "Arial, sans-serif",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = isDarkTheme
                      ? "#444"
                      : "#f5f5f5";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                >
                  {serialNum}
                </div>
              ))}
            </div>
          )}
        </div>
        <div>
          <button
            onClick={() => onAddRow && onAddRow()}
            style={{
              padding: "8px 12px",
              backgroundColor: "#007bff",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "14px",
              fontFamily: "Arial, sans-serif",
            }}
          >
            Add Device
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScanFields;
